import React, { useEffect, useState, useContext } from "react";
import {
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Pagination,
  PaginationItem,
  PaginationLink,
  ModalFooter,
} from "reactstrap";
import Alert from "react-s-alert";
import PsychometricService from "../../services/PsychometricService";
import GeneralService from "../../services/GeneralService";
import ErrorBoundary from "../general/ErrorBoundary";
import { ThemeContext } from "../../contexts/ThemeContext";
import Papa from "papaparse";
import Footer from "../general/Footer";
import PsychometricOrganizationNavbar from "./PyschometricOrganizationNavbar";
import { PsychometricContext } from "../../contexts/PsychometricContext";
import html2pdf from "html2pdf.js";
import PsychometricTestPDFLayout from "./PsychometricTestPDFLayout";
import ReactDOMServer from "react-dom/server";

// Add this CSS somewhere in your component or in a stylesheet
const tooltipStyles = `
  .button-tooltip {
    position: relative;
    display: inline-block;
  }
  
  .button-tooltip .tooltip-text {
    visibility: hidden;
    background-color: rgba(0, 0, 0, 0.8);
    color: white;
    text-align: center;
    padding: 5px 10px;
    border-radius: 6px;
    position: absolute;
    z-index: 1;
    bottom: 125%;
    left: 50%;
    transform: translateX(-50%);
    white-space: nowrap;
  }
  
  .button-tooltip:hover .tooltip-text {
    visibility: visible;
  }
`;

const PsychometricCandidates = (props) => {
  // Define email regex at component level
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // States
  const [isLoading, setIsLoading] = useState(false);
  const { theme, fontFamily, colors, fontWeight } = useContext(ThemeContext);
  const [users, setUsers] = useState([]);
  const [userData, setUserData] = useState({});
  const [modal, setModal] = useState({
    isVisible: false,
    selectedIndex: -1,
    userDetails: {},
  });
  const [editModal, setEditModal] = useState({
    isVisible: false,
    selectedIndex: -1,
    formData: {
      uniqueID: "",
      firstName: "",
      lastName: "",
      gender: "",
      email: "",
    },
  });
  const [createModal, setCreateModal] = useState({
    isVisible: false,
    formData: {
      uniqueID: "",
      firstName: "",
      lastName: "",
      gender: "",
      email: "",
    },
    useGeneratedCode: false,
    error: "",
  });
  const [pagination, setPagination] = useState({
    limit: 10,
    page: 1,
    totalPages: 1,
    totalResults: 0,
  });
  const [searchVal, setSearchVal] = useState("");
  const [csvModal, setCsvModal] = useState({
    isOpen: false,
    useGeneratedCode: false,
  });
  const [csvFile, setCsvFile] = useState(null);
  const [isCsvProcessing, setIsCsvProcessing] = useState(false);
  const { signedOrganization, signedEmail } = useContext(PsychometricContext);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [isOrgPrivate, setIsOrgPrivate] = useState(false);
  const [useGeneratedCode, setUseGeneratedCode] = useState(false);
  const [emailConfirmModal, setEmailConfirmModal] = useState({
    isVisible: false,
    userDetails: null,
  });
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
    isVisible: false,
    selectedIndex: -1,
  });

  // Add effect to handle page refresh
  useEffect(() => {
    const storedOrg = sessionStorage.getItem("signedOrganization");

    if (!signedOrganization && !storedOrg) {
      console.log("No organization found, redirecting...");
      props.history.replace("/psychometricSignIn");
    } else if (!signedOrganization && storedOrg) {
      // If context is empty but we have stored data, use the stored data
      if (searchVal) {
        fetchFilteredUsers(pagination.page, pagination.limit, searchVal);
      } else {
        fetchUsers(pagination.page, pagination.limit);
      }
    }
  }, [signedOrganization]);

  // Modify your existing useEffect
  useEffect(() => {
    const storedOrg = sessionStorage.getItem("signedOrganization");
    const orgToUse = signedOrganization || storedOrg;

    if (!orgToUse) {
      console.log("Redirecting...");
      props.history.replace("/psychometricSignIn");
    } else {
      if (searchVal) {
        fetchFilteredUsers(pagination.page, pagination.limit, searchVal);
      } else {
        fetchUsers(pagination.page, pagination.limit);
      }
    }
  }, [pagination.page, pagination.limit, searchVal]);

  // Add effect to fetch organization privacy setting
  useEffect(() => {
    const fetchOrgPrivacy = async () => {
      try {
        const storedOrg = sessionStorage.getItem("signedOrganization");
        const orgToUse = signedOrganization || storedOrg;
        const storedEmail = sessionStorage.getItem("signedEmail");
        const emailToUse = signedEmail || storedEmail;

        const data = await PsychometricService.getPsychometricOrganization(
          emailToUse
        );
        setIsOrgPrivate(data.user.isPrivate);
      } catch (error) {
        console.error("Error fetching organization privacy setting:", error);
      }
    };
    fetchOrgPrivacy();
  }, [signedOrganization, signedEmail]);

  // Modify fetchUsers to use stored organization if context is empty
  const fetchUsers = async (page = 1, limit = 10) => {
    setIsLoading(true);
    try {
      const storedOrg = sessionStorage.getItem("signedOrganization");
      const orgToUse = signedOrganization || storedOrg;

      const data = await PsychometricService.getUsersOrg(orgToUse, page, limit);
      setUsers(data.users);
      setPagination((prev) => ({
        ...prev,
        totalPages: data.pagination.totalPages,
        totalResults: data.pagination.totalResults,
        page: data.pagination.page,
      }));
      setIsLoading(false);
    } catch (err) {
      Alert.error(
        "Some error occurred, please try again later or report to the support team.",
        {
          position: "top-right",
          effect: "slide",
        }
      );
      GeneralService.logError(
        "at PsychometricUsers fetchUsers",
        "Error in try catch block",
        err.stack
      );
      setIsLoading(false);
    }
  };

  // Modify fetchFilteredUsers similarly
  const fetchFilteredUsers = async (page = 1, limit = 10, search = searchVal) => {
    setIsLoading(true);
    try {
      const storedOrg = sessionStorage.getItem("signedOrganization");
      const orgToUse = signedOrganization || storedOrg;

      const data = await PsychometricService.getFilteredUsersOrg(
        orgToUse,
        page,
        limit,
        search
      );
      setUsers(data.users);
      setPagination((prev) => ({
        ...prev,
        totalPages: data.pagination.totalPages,
        totalResults: data.pagination.totalResults,
        page: data.pagination.page,
      }));
      setIsLoading(false);
    } catch (err) {
      Alert.error(
        "Some error occurred, please try again later or report to the support team.",
        {
          position: "top-right",
          effect: "slide",
        }
      );
      GeneralService.logError(
        "at PsychometricUsers fetchFilteredUsers",
        "Error in try catch block",
        err.stack
      );
      setIsLoading(false);
    }
  };

  const generatePDF = async (index) => {
    try {
      const userData = users[index];
      console.log("Generating PDF with userData:");

      // Check if user has completed the test
      if (!userData.hasCompletedTest) {
        Alert.error("User has not taken test yet", {
          position: "top-right",
          effect: "slide",
        });
        return;
      }

      // Create a temporary div to render the PDF layout
      const element = document.createElement("div");
      element.innerHTML = ReactDOMServer.renderToString(
        <PsychometricTestPDFLayout userData={userData} />
      );
      document.body.appendChild(element);

      const opt = {
        margin: 10,
        filename: `psychometric-report-${userData.firstName}-${userData.lastName}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          logging: true,
        },
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
        },
        output: "blob",
      };

      try {
        // Generate PDF as blob
        const pdfBlob = await html2pdf()
          .set(opt)
          .from(element)
          .save()
          .output("blob");

        // Create blob URL
        const blobUrl = URL.createObjectURL(pdfBlob);

        // Open in new tab
        window.open(blobUrl, "_blank");

        // Clean up blob URL after a delay
        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
      } catch (error) {
        console.error("Error in PDF generation:", error);
      } finally {
        // Clean up the temporary element
        document.body.removeChild(element);
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      Alert.error("Failed to generate PDF report");
    }
  };

  // ✅ NEW: Export current table (users state) to CSV
  const handleExportCSV = () => {
    if (!users || users.length === 0) {
      Alert.error("No candidates to export", {
        position: "top-right",
        effect: "slide",
      });
      return;
    }

    const exportData = users.map((u) => ({
      "User Code": u.uniqueID || "",
      "First Name": u.firstName || "",
      "Last Name": u.lastName || "",
      Gender: u.gender || "",
      ...(isOrgPrivate ? {} : { Email: u.email || "" }),
      Organization: u.organization || "",
      "Test Completed": u.hasCompletedTest ? "Yes" : "No",
    }));

    const csv = Papa.unparse(exportData);

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `candidates-${signedOrganization || "export"}-${Date.now()}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);

    Alert.success("CSV exported successfully", {
      position: "top-right",
      effect: "slide",
    });
  };

  // Add this helper function at component level
  const generateRandomId = () => {
    const chars = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    let result = "";
    for (let i = 0; i < 6; i++) {
      result += chars[Math.floor(Math.random() * chars.length)];
    }
    return result;
  };

  // Function to handle search input change
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchVal(value);
    setPagination({ ...pagination, page: 1 });
    fetchFilteredUsers(1, pagination.limit, value); // Fetch filtered users with the updated search value
  };

  // Function to handle pagination click
  const handlePagination = async (e, index) => {
    e.preventDefault();
    setIsPaginationLoading(true);
    setPagination((prev) => ({ ...prev, page: index }));

    try {
      if (searchVal) {
        await fetchFilteredUsers(index, pagination.limit, searchVal);
      } else {
        await fetchUsers(index, pagination.limit);
      }
    } catch (error) {
      console.error("Error during pagination:", error);
    } finally {
      setIsPaginationLoading(false);
    }
  };

  // Function to show details modal
  const showDetails = (index) => {
    setModal({
      isVisible: true,
      selectedIndex: index,
      userDetails: users[index],
    });
  };

  // Function to toggle edit modal
  const toggleEditModal = (index) => {
    setEditModal({
      isVisible: true,
      selectedIndex: index,
      formData: {
        uniqueID: users[index].uniqueID,
        firstName: users[index].firstName,
        lastName: users[index].lastName,
        gender: users[index].gender,
        email: users[index].email || "",
      },
    });
  };

  // Function to handle edit form changes
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditModal({
      ...editModal,
      formData: {
        ...editModal.formData,
        [name]: value,
      },
    });
  };

  // Function to handle edit form submission
  const handleEditSubmit = async (e) => {
    e.preventDefault();

    const userId = users[editModal.selectedIndex]._id;
    try {
      const result = await PsychometricService.updateUser(
        userId,
        editModal.formData
      );

      if (result.success) {
        const updatedUsers = [...users];
        updatedUsers[editModal.selectedIndex] = result.user;
        setUsers(updatedUsers);
        setEditModal({ ...editModal, isVisible: false });
        Alert.success("User details updated successfully");
      } else {
        Alert.error(result.message || "Failed to update user details");
      }
    } catch (error) {
      console.error("Error updating user:", error);
      Alert.error("Failed to update user details");
    }
  };

  // Function to handle delete user click
  const handleDeleteClick = (index) => {
    setDeleteConfirmModal({
      isVisible: true,
      selectedIndex: index,
    });
  };

  // Function to handle confirmed deletion
  const handleConfirmedDelete = () => {
    handleDelete(deleteConfirmModal.selectedIndex);
    setDeleteConfirmModal({
      isVisible: false,
      selectedIndex: -1,
    });
  };

  // Function to toggle create modal
  const toggleCreateModal = () => {
    setCreateModal({
      ...createModal,
      isVisible: !createModal.isVisible,
      error: "",
    });
  };

  // Function to handle create form changes
  const handleCreateChange = (e) => {
    const { name, value } = e.target;
    setCreateModal({
      ...createModal,
      formData: {
        ...createModal.formData,
        [name]: value,
      },
    });
  };

  // Function to handle create form submission
  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    const { uniqueID, firstName, lastName, gender, email } =
      createModal.formData;
    try {
      setCreateModal((prev) => ({ ...prev, error: "" }));

      let finalUniqueID = uniqueID;
      if (isOrgPrivate && !createModal.useGeneratedCode) {
        try {
          const checkResult = await PsychometricService.checkId(uniqueID);
          if (checkResult.exists) {
            setCreateModal((prev) => ({
              ...prev,
              error: "This User Code already exists. Please use a different one.",
            }));
            return;
          }
        } catch (error) {
          console.error("Error checking User Code:", error);
          throw error;
        }
      }

      if (!isOrgPrivate || (isOrgPrivate && createModal.useGeneratedCode)) {
        if (!isOrgPrivate && !email) {
          setCreateModal((prev) => ({ ...prev, error: "Email is required" }));
          return;
        }
        let isUnique = false;
        while (!isUnique) {
          finalUniqueID = generateRandomId();
          try {
            const checkResult = await PsychometricService.checkId(finalUniqueID);
            isUnique = !checkResult.exists;
          } catch (error) {
            console.error("Error checking ID:", error);
            throw error;
          }
        }
      } else if (isOrgPrivate && !createModal.useGeneratedCode && !uniqueID) {
        setCreateModal((prev) => ({ ...prev, error: "User Code is required" }));
        return;
      }

      const result = isOrgPrivate
        ? await PsychometricService.createCandidate(
            finalUniqueID,
            firstName,
            lastName,
            gender,
            undefined,
            signedOrganization
          )
        : await PsychometricService.createCandidate(
            finalUniqueID,
            firstName,
            lastName,
            gender,
            email,
            signedOrganization
          );

      if (result.candidateExists) {
        Alert.error(result.message.msgBody);
      } else {
        Alert.success(result.message.msgBody);
        toggleCreateModal();
        fetchUsers();
      }
    } catch (error) {
      console.error("Error creating candidate:", error);
      setCreateModal((prev) => ({ ...prev, error: "Failed to create candidate" }));
    }
  };

  // Function to toggle CSV import modal
  const toggleCsvModal = () => {
    setCsvModal({ ...csvModal, isOpen: !csvModal.isOpen });
  };

  // Function to handle CSV file input change
  const handleCsvChange = (e) => {
    setCsvFile(e.target.files[0]);
  };

  // Function to generate a unique code
  const generateUniqueCode = async () => {
    let code;
    let isUnique = false;

    while (!isUnique) {
      const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
      code = Array.from({ length: 6 }, () =>
        characters.charAt(Math.floor(Math.random() * characters.length))
      ).join("");

      try {
        const exists = await PsychometricService.checkId(code);
        if (!exists.exists) {
          isUnique = true;
        }
      } catch (error) {
        console.error("Error checking code uniqueness:", error);
        throw new Error("Failed to generate unique code");
      }
    }

    return code;
  };

  // Modified CSV import handler
  const handleCsvSubmit = async (e) => {
    e.preventDefault();
    if (!csvFile) {
      Alert.error("Please select a CSV file");
      return;
    }

    setIsCsvProcessing(true);
    let successCount = 0;
    let failedRows = [];

    try {
      Papa.parse(csvFile, {
        header: true,
        skipEmptyLines: true,
        complete: async (results) => {
          try {
            const { data } = results;
            for (let index = 0; index < data.length; index++) {
              const row = data[index];
              try {
                const firstName =
                  row.firstName || row.first_name || row["First Name"] || "";
                const lastName =
                  row.lastName || row.last_name || row["Last Name"] || "";
                const gender = row.gender || row.Gender || "";
                const email = row.email || row.Email || "";
                const userCode =
                  row.userCode || row.user_code || row["User Code"] || "";

                const missingFields = [];
                if (!firstName) missingFields.push("First Name");
                if (!lastName) missingFields.push("Last Name");
                if (!gender) missingFields.push("Gender");
                if (!isOrgPrivate && !email) missingFields.push("Email");
                if (
                  isOrgPrivate &&
                  !csvModal.useGeneratedCode &&
                  !userCode
                )
                  missingFields.push("User Code");

                if (email && !emailRegex.test(email)) {
                  failedRows.push({
                    rowNumber: index + 2,
                    error: `Invalid email format: ${email}`,
                    data: row,
                  });
                  continue;
                }

                if (missingFields.length > 0) {
                  failedRows.push({
                    rowNumber: index + 2,
                    error: `Missing required fields: ${missingFields.join(
                      ", "
                    )}`,
                    data: row,
                  });
                  continue;
                }

                const userData = {
                  firstName,
                  lastName,
                  gender,
                  email: isOrgPrivate ? undefined : email,
                  uniqueID: csvModal.useGeneratedCode
                    ? await generateUniqueCode()
                    : userCode || (await generateUniqueCode()),
                  organization: signedOrganization,
                };

                await PsychometricService.createCandidate(
                  userData.uniqueID,
                  userData.firstName,
                  userData.lastName,
                  userData.gender,
                  userData.email,
                  userData.organization
                );

                successCount++;
              } catch (error) {
                failedRows.push({
                  rowNumber: index + 2,
                  error: error.message || "Failed to create user",
                  data: row,
                });
              }
            }

            if (successCount > 0) {
              Alert.success(`Successfully imported ${successCount} users`);
            }

            if (failedRows.length > 0) {
              const failureMessage =
                `Failed to import ${failedRows.length} users:\n` +
                failedRows
                  .map((row) => `Row ${row.rowNumber}: ${row.error}`)
                  .join("\n");
              Alert.error(failureMessage);
            }

            if (successCount > 0) {
              fetchUsers(pagination.page, pagination.limit);
            }

            setIsCsvProcessing(false);
            setCsvModal({ isOpen: false, useGeneratedCode: false });
          } catch (error) {
            console.error("Error processing CSV data:", error);
            Alert.error("Error processing CSV file");
            setIsCsvProcessing(false);
          }
        },
        error: (error) => {
          console.error("CSV parsing error:", error);
          Alert.error("Error parsing CSV file");
          setIsCsvProcessing(false);
        },
      });
    } catch (error) {
      console.error("Error processing CSV file:", error);
      Alert.error("Failed to process CSV file.");
      setIsCsvProcessing(false);
    }
  };

  // Add new function to handle sending email
  const handleSendEmail = async (userId) => {
    const user = users.find((u) => u._id === userId);

    if (!user.email) {
      Alert.error("User has no email address", {
        position: "top-right",
        effect: "slide",
      });
      return;
    }

    setEmailConfirmModal({
      isVisible: true,
      userDetails: user,
    });
  };

  // Add new function to handle actual email sending
  const confirmAndSendEmail = async () => {
    try {
      setIsLoading(true);
      const result = await PsychometricService.sendUserCodeEmail(
        emailConfirmModal.userDetails._id
      );
      Alert.success(result.message.msgBody);
      setEmailConfirmModal({ isVisible: false, userDetails: null });
    } catch (error) {
      console.error("Error sending email:", error);
      Alert.error(
        error.message?.msgBody ||
          "Failed to send email. Please verify the email address is correct.",
        {
          position: "top-right",
          effect: "slide",
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Add the handleDelete function
  const handleDelete = async (index) => {
    try {
      const userId = users[index]._id;
      const result = await PsychometricService.deleteCandidate(userId);

      if (result.success) {
        const updatedUsers = users.filter((_, i) => i !== index);
        setUsers(updatedUsers);
        Alert.success("User deleted successfully");
      } else {
        Alert.error(result.message || "Failed to delete user");
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      Alert.error("Failed to delete user");
    }
  };

  return (
    <ErrorBoundary>
      <style>{tooltipStyles}</style>
      <div className="psychometric-users" style={{ fontFamily, color: colors.text }}>
        <PsychometricOrganizationNavbar />
        <h2 style={{ fontWeight, paddingLeft: "20px" }}>Manage Candidates</h2>

        <div className="card mb-4">
          <div className="card-header">Candidate List</div>
          <div className="card-body py-0">
            <div
              className="mb-3 d-flex justify-content-between align-items-center"
              style={{ marginTop: "20px" }}
            >
              {/* Search bar */}
              <input
                type="text"
                className="form-control"
                placeholder="Search"
                value={searchVal}
                onChange={handleSearch}
                style={{ width: "300px" }}
              />

              {/* Buttons */}
              <div>
                <Button
                  color="primary"
                  style={theme.formButton}
                  onClick={toggleCreateModal}
                  className="mr-2"
                >
                  Add New Candidate
                </Button>{" "}
                <Button
                  color="secondary"
                  style={theme.formButton}
                  onClick={toggleCsvModal}
                  className="mr-2"
                >
                  Import Users from CSV
                </Button>

                {/* ✅ NEW Export CSV Button */}
                <Button
                  color="success"
                  style={theme.formButton}
                  onClick={handleExportCSV}
                >
                  Export CSV
                </Button>
              </div>
            </div>

            {/* Table */}
            <Table striped bordered responsive>
              <thead>
                <tr>
                  <th>User Code</th>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>Gender</th>
                  {!isOrgPrivate && <th>Email</th>}
                  <th>Organisation</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user, index) => (
                  <tr key={index}>
                    <td>{user.uniqueID}</td>
                    <td>{user.firstName}</td>
                    <td>{user.lastName}</td>
                    <td>{user.gender}</td>
                    {!isOrgPrivate && <td>{user.email}</td>}
                    <td>{user.organization}</td>
                    <td>
                      <div style={{ display: "flex" }}>
                        <div className="button-tooltip" style={{ position: "relative", marginRight: "1em" }}>
                          <Button
                            id="view-details-button"
                            style={{ ...theme.formButton, padding: "5px", fontSize: "16px" }}
                            onClick={() => showDetails(index)}
                          >
                            <i className="fas fa-eye" />
                          </Button>
                          <span className="tooltip-text" style={{ display: "inline-block" }}>
                            View candidate details
                          </span>
                        </div>

                        {!isOrgPrivate && (
                          <div className="button-tooltip" style={{ position: "relative", marginRight: "1em" }}>
                            <Button
                              id="send-code-button"
                              style={{ ...theme.formButton, padding: "5px", fontSize: "16px" }}
                              onClick={() => handleSendEmail(user._id)}
                            >
                              <i className="fas fa-envelope" />
                            </Button>
                            <span className="tooltip-text" style={{ display: "inline-block" }}>
                              Send access code
                            </span>
                          </div>
                        )}

                        <div className="button-tooltip" style={{ position: "relative", marginRight: "1em" }}>
                          <Button
                            id="edit-button"
                            style={{ ...theme.formButton, padding: "5px", fontSize: "16px" }}
                            onClick={() => toggleEditModal(index)}
                          >
                            <i className="fas fa-edit" />
                          </Button>
                          <span className="tooltip-text" style={{ display: "inline-block" }}>
                            Edit candidate details
                          </span>
                        </div>

                        <div className="button-tooltip" style={{ position: "relative", marginRight: "1em" }}>
                          <Button
                            id="delete-button"
                            style={{ ...theme.formButton, padding: "5px", fontSize: "16px" }}
                            onClick={() => handleDeleteClick(index)}
                          >
                            <i className="fas fa-trash" />
                          </Button>
                          <span className="tooltip-text" style={{ display: "inline-block" }}>
                            Delete candidate
                          </span>
                        </div>

                        <div className="button-tooltip" style={{ position: "relative", marginRight: "1em" }}>
                          <Button
                            id="download-report-button"
                            style={{ ...theme.formButton, padding: "5px", fontSize: "16px" }}
                            onClick={() => generatePDF(index)}
                            disabled={!user.hasCompletedTest}
                          >
                            <i className="fas fa-download" />
                          </Button>
                          <span
                            className="tooltip-text"
                            style={{ display: user.hasCompletedTest ? "none" : "inline-block" }}
                          >
                            Test not completed by candidate
                          </span>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {/* Pagination */}
            <div className="d-flex justify-content-center mt-4 mb-4">
              <Pagination aria-label="Users pagination">
                <PaginationItem disabled={pagination.page === 1 || isPaginationLoading}>
                  <PaginationLink
                    previous
                    onClick={(e) => handlePagination(e, pagination.page - 1)}
                    style={{
                      backgroundColor: "white",
                      color: colors.newPrimary,
                      borderColor: colors.newPrimary,
                      cursor: pagination.page === 1 || isPaginationLoading ? "not-allowed" : "pointer",
                      fontFamily: fontFamily.primary,
                      fontWeight: fontWeight.medium,
                      opacity: isPaginationLoading ? 0.7 : 1,
                    }}
                  />
                </PaginationItem>

                {[...Array(pagination.totalPages).keys()].map((page, index) => (
                  <PaginationItem key={index} active={page + 1 === pagination.page} disabled={isPaginationLoading}>
                    <PaginationLink
                      onClick={(e) => handlePagination(e, page + 1)}
                      style={{
                        backgroundColor: page + 1 === pagination.page ? colors.newPrimary : "white",
                        color: page + 1 === pagination.page ? "white" : colors.newPrimary,
                        borderColor: colors.newPrimary,
                        cursor: isPaginationLoading ? "not-allowed" : "pointer",
                        fontFamily: fontFamily.primary,
                        fontWeight: page + 1 === pagination.page ? fontWeight.semiBold : fontWeight.medium,
                        opacity: isPaginationLoading ? 0.7 : 1,
                        position: "relative",
                      }}
                    >
                      {page + 1}
                      {isPaginationLoading && page + 1 === pagination.page && (
                        <div
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <div
                            className="spinner-border spinner-border-sm"
                            style={{
                              width: "1rem",
                              height: "1rem",
                              borderColor: "white",
                              borderRightColor: "transparent",
                            }}
                            role="status"
                          >
                            <span className="sr-only">Loading...</span>
                          </div>
                        </div>
                      )}
                    </PaginationLink>
                  </PaginationItem>
                ))}

                <PaginationItem disabled={pagination.page === pagination.totalPages || isPaginationLoading}>
                  <PaginationLink
                    next
                    onClick={(e) => handlePagination(e, pagination.page + 1)}
                    style={{
                      backgroundColor: "white",
                      color: colors.newPrimary,
                      borderColor: colors.newPrimary,
                      cursor:
                        pagination.page === pagination.totalPages || isPaginationLoading ? "not-allowed" : "pointer",
                      fontFamily: fontFamily.primary,
                      fontWeight: fontWeight.medium,
                      opacity: isPaginationLoading ? 0.7 : 1,
                    }}
                  />
                </PaginationItem>
              </Pagination>
            </div>

            {/* Add loading overlay for the table */}
            {isPaginationLoading && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.7)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 1000,
                }}
              >
                <div
                  className="spinner-border"
                  style={{
                    color: colors.newPrimary,
                    width: "3rem",
                    height: "3rem",
                  }}
                  role="status"
                >
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            )}

            {/* Modals */}
            {/* User Details Modal */}
            <Modal isOpen={modal.isVisible} toggle={() => setModal({ ...modal, isVisible: false })}>
              <ModalHeader toggle={() => setModal({ ...modal, isVisible: false })}>User Details</ModalHeader>
              <ModalBody>
                <p>
                  <strong>User Code:</strong> {modal.userDetails.uniqueID}
                </p>
                {!isOrgPrivate && (
                  <p>
                    <strong>Email:</strong> {modal.userDetails.email}
                  </p>
                )}
                <p>
                  <strong>First Name:</strong> {modal.userDetails.firstName}
                </p>
                <p>
                  <strong>Last Name:</strong> {modal.userDetails.lastName}
                </p>
                <p>
                  <strong>Gender:</strong> {modal.userDetails.gender}
                </p>
                <p>
                  <strong>Organization:</strong> {modal.userDetails.organization}
                </p>
              </ModalBody>
            </Modal>

            {/* Edit User Modal */}
            <Modal isOpen={editModal.isVisible} toggle={() => setEditModal({ ...editModal, isVisible: false })}>
              <ModalHeader toggle={() => setEditModal({ ...editModal, isVisible: false })}>Edit User</ModalHeader>
              <ModalBody>
                <form onSubmit={handleEditSubmit}>
                  {!isOrgPrivate && (
                    <div className="form-group">
                      <label>Email</label>
                      <input
                        type="email"
                        className="form-control"
                        name="email"
                        value={editModal.formData.email}
                        onChange={handleEditChange}
                        required
                      />
                    </div>
                  )}
                  <div className="form-group">
                    <label>First Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="firstName"
                      value={editModal.formData.firstName}
                      onChange={handleEditChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="lastName"
                      value={editModal.formData.lastName}
                      onChange={handleEditChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Gender</label>
                    <select
                      className="form-control"
                      name="gender"
                      value={editModal.formData.gender}
                      onChange={handleEditChange}
                      required
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>
                  <Button type="submit" style={theme.formButton} color="primary">
                    Update User
                  </Button>{" "}
                  <Button
                    color="secondary"
                    style={theme.formButton}
                    onClick={() => setEditModal({ ...editModal, isVisible: false })}
                  >
                    Cancel
                  </Button>
                </form>
              </ModalBody>
            </Modal>

            {/* Create User Modal */}
            <Modal isOpen={createModal.isVisible} toggle={toggleCreateModal}>
              <ModalHeader toggle={toggleCreateModal}>Add New Candidate</ModalHeader>
              <ModalBody>
                {createModal.error && (
                  <div className="alert alert-danger" role="alert">
                    {createModal.error}
                  </div>
                )}
                <form onSubmit={handleCreateSubmit}>
                  {isOrgPrivate && (
                    <div className="form-group mb-3">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="useGeneratedCode"
                          checked={createModal.useGeneratedCode}
                          onChange={(e) =>
                            setCreateModal({
                              ...createModal,
                              useGeneratedCode: e.target.checked,
                              formData: {
                                ...createModal.formData,
                                uniqueID: e.target.checked ? "" : createModal.formData.uniqueID,
                              },
                            })
                          }
                        />
                        <label className="form-check-label" htmlFor="useGeneratedCode">
                          Use Generated User Code
                        </label>
                      </div>
                    </div>
                  )}
                  {isOrgPrivate && !createModal.useGeneratedCode && (
                    <div className="form-group">
                      <label>User Code *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="uniqueID"
                        value={createModal.formData.uniqueID}
                        onChange={handleCreateChange}
                        required
                      />
                    </div>
                  )}
                  {!isOrgPrivate && (
                    <div className="form-group">
                      <label>Email *</label>
                      <input
                        type="email"
                        className="form-control"
                        name="email"
                        value={createModal.formData.email}
                        onChange={handleCreateChange}
                        required
                      />
                    </div>
                  )}
                  <div className="form-group">
                    <label>First Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="firstName"
                      value={createModal.formData.firstName}
                      onChange={handleCreateChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="lastName"
                      value={createModal.formData.lastName}
                      onChange={handleCreateChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Gender</label>
                    <select
                      className="form-control"
                      name="gender"
                      value={createModal.formData.gender}
                      onChange={handleCreateChange}
                      required
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>
                  <Button type="submit" style={theme.formButton} color="primary">
                    Add User
                  </Button>{" "}
                  <Button color="secondary" style={theme.formButton} onClick={toggleCreateModal}>
                    Cancel
                  </Button>
                </form>
              </ModalBody>
            </Modal>

            {/* CSV Import Modal */}
            <Modal isOpen={csvModal.isOpen} toggle={toggleCsvModal}>
              <ModalHeader toggle={toggleCsvModal}>Import Users from CSV</ModalHeader>
              <ModalBody>
                {isOrgPrivate && (
                  <div className="form-group mb-3">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="csvUseGeneratedCode"
                        checked={csvModal.useGeneratedCode}
                        onChange={(e) =>
                          setCsvModal({
                            ...csvModal,
                            useGeneratedCode: e.target.checked,
                          })
                        }
                      />
                      <label className="form-check-label" htmlFor="csvUseGeneratedCode">
                        Generate User Codes Automatically
                      </label>
                    </div>
                  </div>
                )}

                <p>Upload a CSV file with the following columns:</p>
                <ul>
                  <li>First Name (or firstName or first_name)</li>
                  <li>Last Name (or lastName or last_name)</li>
                  <li>Gender</li>
                  {!isOrgPrivate && <li>Email - Required for public organizations</li>}
                  {isOrgPrivate && (
                    <li>
                      User Code (or userCode or user_code) -{" "}
                      {csvModal.useGeneratedCode ? "Will be generated automatically" : "Required for each user"}
                    </li>
                  )}
                </ul>

                <form onSubmit={handleCsvSubmit}>
                  <div className="form-group">
                    <input type="file" onChange={handleCsvChange} accept=".csv" />
                  </div>
                  <Button type="submit" style={theme.formButton} color="primary" disabled={isCsvProcessing}>
                    {isCsvProcessing ? "Processing..." : "Import CSV"}
                  </Button>{" "}
                  <Button color="secondary" style={theme.formButton} onClick={toggleCsvModal}>
                    Cancel
                  </Button>
                </form>
              </ModalBody>
            </Modal>

            {/* Email Confirmation Modal */}
            <Modal
              isOpen={emailConfirmModal.isVisible}
              toggle={() => setEmailConfirmModal({ isVisible: false, userDetails: null })}
            >
              <ModalHeader toggle={() => setEmailConfirmModal({ isVisible: false, userDetails: null })}>
                Send User Code
              </ModalHeader>
              <ModalBody>
                {emailConfirmModal.userDetails && (
                  <>
                    <p>Are you sure you want to send the user code to:</p>
                    <p>
                      <strong>Name:</strong> {emailConfirmModal.userDetails.firstName}{" "}
                      {emailConfirmModal.userDetails.lastName}
                    </p>
                    <p>
                      <strong>Email:</strong> {emailConfirmModal.userDetails.email}
                    </p>
                    <p>
                      <strong>User Code:</strong> {emailConfirmModal.userDetails.uniqueID}
                    </p>
                  </>
                )}
              </ModalBody>
              <ModalFooter>
                <Button
                  color="primary"
                  style={theme.formButton}
                  onClick={confirmAndSendEmail}
                  disabled={isLoading || !emailConfirmModal.userDetails?.email}
                >
                  {isLoading ? "Sending..." : "Send User Code"}
                </Button>{" "}
                <Button
                  color="secondary"
                  style={theme.formButton}
                  onClick={() => setEmailConfirmModal({ isVisible: false, userDetails: null })}
                >
                  Cancel
                </Button>
              </ModalFooter>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
              isOpen={deleteConfirmModal.isVisible}
              toggle={() => setDeleteConfirmModal({ isVisible: false, selectedIndex: -1 })}
            >
              <ModalHeader toggle={() => setDeleteConfirmModal({ isVisible: false, selectedIndex: -1 })}>
                Confirm Delete
              </ModalHeader>
              <ModalBody>
                {deleteConfirmModal.selectedIndex !== -1 && (
                  <>
                    <p>Are you sure you want to delete this candidate?</p>
                    <p>
                      <strong>Name:</strong>{" "}
                      {users[deleteConfirmModal.selectedIndex]?.firstName}{" "}
                      {users[deleteConfirmModal.selectedIndex]?.lastName}
                    </p>
                    <p>
                      <strong>User Code:</strong> {users[deleteConfirmModal.selectedIndex]?.uniqueID}
                    </p>
                  </>
                )}
              </ModalBody>
              <ModalFooter>
                <Button color="danger" style={theme.formButton} onClick={handleConfirmedDelete}>
                  Delete
                </Button>{" "}
                <Button
                  color="secondary"
                  style={theme.formButton}
                  onClick={() => setDeleteConfirmModal({ isVisible: false, selectedIndex: -1 })}
                >
                  Cancel
                </Button>
              </ModalFooter>
            </Modal>
          </div>
        </div>

        <Footer />
      </div>
    </ErrorBoundary>
  );
};

export default PsychometricCandidates;
